/**
 * 当前激活页（currentPageId）属性：尺寸、背景、辅助线。
 * Canvas 指根设计器组件；本面板编辑的是 Page，不是「画布板面」混称。
 */
import { useRef, useState } from 'react';
import { Button, InputGroup, toast } from '@heroui/react';
import { ColorPickerField } from '@/components/custom';
import { useCanvasAssetAdapters } from '@/components/canvas/designer/shell/CanvasAssetContext';
import { useCanvasMode } from '@/components/canvas/designer/shell/CanvasModeContext';
import { resolvePageGuideSettings } from '@/components/canvas/page/guideSettings';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { PageBackground } from '@/components/canvas/document/publicTypes';
import { CanvasGuideSettingsSection } from './CanvasGuideSettingsSection';

function resolveImageUnderlayColor(bg: PageBackground): string {
  if (bg.type === 'color' && bg.value) {
    const v = bg.value;
    if (!v.startsWith('linear-gradient(') && !v.startsWith('radial-gradient(')) {
      return v;
    }
  }
  if ((bg.type === 'image' || bg.type === 'transparent') && bg.backgroundColor) {
    return bg.backgroundColor;
  }
  if (bg.type === 'gradient' && bg.value) {
    const hex = bg.value.match(/#(?:[0-9a-f]{3,8})\b/i);
    const rgb = bg.value.match(/rgba?\([^)]+\)/i);
    if (hex) return hex[0];
    if (rgb) return rgb[0];
  }
  return '#ffffff';
}

export function PageProperties() {
  const {
    getCurrentPage,
    setPageSize,
    setPageBackground,
    setPageGuidesEnabled,
    patchPageGuideGrid,
  } = useCanvas();
  const page = getCurrentPage();
  const mode = useCanvasMode();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage } = useCanvasAssetAdapters();
  const [isBackgroundImageUploading, setIsBackgroundImageUploading] = useState(false);

  if (!page) return null;

  const { size, background } = page;
  const canEditPageSize = mode !== 'slide';
  const guideSettings = resolvePageGuideSettings(page);
  const hasBackgroundImage = background.type === 'image' && !!background.value;
  const fillValue =
    background.type === 'color'
      ? (background.value ?? '#ffffff')
      : background.type === 'gradient' && background.value
        ? background.value
        : background.type === 'image'
          ? (background.backgroundColor ?? '#ffffff')
          : '#ffffff';

  const handleWidthChange = (v: number) => {
    if (!canEditPageSize) return;
    const n = Math.max(1, Math.floor(v));
    setPageSize({ width: n, height: size.height });
  };

  const handleHeightChange = (v: number) => {
    if (!canEditPageSize) return;
    const n = Math.max(1, Math.floor(v));
    setPageSize({ width: size.width, height: n });
  };

  const handleFillChange = (value: string) => {
    const isGradient =
      value.startsWith('linear-gradient(') || value.startsWith('radial-gradient(');
    if (background.type === 'image') {
      setPageBackground({
        type: 'image',
        value: background.value,
        imagePositionX: background.imagePositionX,
        imagePositionY: background.imagePositionY,
        backgroundColor: isGradient ? '#ffffff' : value,
      });
    } else if (isGradient) {
      setPageBackground({ type: 'gradient', value });
    } else {
      setPageBackground({ type: 'color', value });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file?.type.startsWith('image/')) return;
    if (!uploadImage) {
      toast.warning('当前环境未配置图片上传');
      return;
    }
    setIsBackgroundImageUploading(true);
    try {
      const url = await uploadImage(file);
      setPageBackground({
        type: 'image',
        value: url,
        imagePositionX: background.type === 'image' ? (background.imagePositionX ?? 50) : 50,
        imagePositionY: background.type === 'image' ? (background.imagePositionY ?? 50) : 50,
        backgroundColor: resolveImageUnderlayColor(background),
      });
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : '背景图上传失败');
    } finally {
      setIsBackgroundImageUploading(false);
    }
  };

  const handleDeleteBackgroundImage = () => {
    setPageBackground({
      type: 'color',
      value:
        typeof fillValue === 'string' &&
        !fillValue.startsWith('linear-gradient') &&
        !fillValue.startsWith('radial-gradient')
          ? fillValue
          : '#ffffff',
    });
  };

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-6 p-1">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-default-foreground">页面尺寸</h3>
        <div className="flex flex-row gap-1">
          <InputGroup aria-label="页面宽度">
            <InputGroup.Prefix>宽</InputGroup.Prefix>
            <InputGroup.Input
              type="number"
              min={1}
              step={1}
              value={size.width}
              disabled={!canEditPageSize}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              className="w-[50%]"
            />
            <InputGroup.Suffix>px</InputGroup.Suffix>
          </InputGroup>
          <InputGroup aria-label="页面高度">
            <InputGroup.Prefix>高</InputGroup.Prefix>
            <InputGroup.Input
              type="number"
              min={1}
              step={1}
              value={size.height}
              disabled={!canEditPageSize}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              className="w-[50%]"
            />
            <InputGroup.Suffix>px</InputGroup.Suffix>
          </InputGroup>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-default-foreground">背景图片</h3>
        <div className="flex flex-wrap gap-2">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          <Button
            variant="outline"
            size="sm"
            isDisabled={isBackgroundImageUploading}
            onPress={() => imageInputRef.current?.click()}
          >
            {isBackgroundImageUploading ? '上传中…' : hasBackgroundImage ? '替换图片' : '上传图片'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            isDisabled={!hasBackgroundImage}
            onPress={handleDeleteBackgroundImage}
          >
            删除背景图
          </Button>
        </div>
        {hasBackgroundImage ? (
          <p className="mt-2 text-xs text-muted">双击页面可移动背景图片位置</p>
        ) : null}
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-default-foreground">
          {hasBackgroundImage ? '背景底色' : '背景颜色'}
        </h3>
        {hasBackgroundImage ? (
          <p className="mb-2 text-xs text-muted">背景颜色会作为图片的底色显示</p>
        ) : null}
        <ColorPickerField
          aria-label={hasBackgroundImage ? '页面背景底色' : '页面背景颜色'}
          value={String(fillValue)}
          onChange={handleFillChange}
          variant="secondary"
        />
      </div>

      <CanvasGuideSettingsSection
        guideSettings={guideSettings}
        onVisibleChange={setPageGuidesEnabled}
        onGridChange={(patch) => patchPageGuideGrid(patch)}
      />
    </div>
  );
}
